import cn from 'classnames';
import { useConfig } from 'config/use-config';
import useMobile from 'hooks/common/use-mobile';
import { CollectionProduct } from 'models/collection/collection';
import { useRouter } from 'next/router';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Gtm } from 'tracking';
import { slugify } from 'utils/strings';
import SmallCaps from '../../../../components/typography/small-caps';
import {
  CollectionFilter,
  FilterConfig,
  possibleTags,
} from '../../../../interfaces/collection-filter';
import { useAvailableFilterMap } from '../../state/available-choices';
import styles from './filter-group.module.css';

interface IFilterGroupProps {
  filterConfig: FilterConfig;
  appliedFilter: string[];
  toggleFilter: (value: string) => void;
  collectionProducts: CollectionProduct[];
  visibleProducts: CollectionProduct[];
}

/**
 * Component that renders a the filters grouped by the filter key.
 * AT the moment we use "fake filters" to test the following fake door test.
 * https://petsdeli.atlassian.net/browse/PT-5632
 * These filters just track the user interaction and don't filter the products.
 */
const FilterGroup: React.FC<IFilterGroupProps> = ({
  filterConfig,
  appliedFilter,
  toggleFilter,
  collectionProducts,
  visibleProducts,
}) => {
  const { asPath } = useRouter();
  const { commonCollections, commonPagePaths } = useConfig();
  const isMobile = useMobile();

  const isCatsWetFoodPage = asPath.includes(commonCollections.catsWetFood);
  const isCatsDryFoodPage = asPath.includes(commonCollections.catsDryFood);
  const isSnacksPage = asPath.includes(commonCollections.catsSnacks);
  const isCatHomePage = asPath.includes(commonPagePaths.catsHome);

  const availableFilterMap = useAvailableFilterMap(collectionProducts, filterConfig);

  // Remove 'line' and 'sorts' filters from filterConfig if on cats wet food or dry food page
  const filteredAvailableFilterMap = useMemo(() => {
    if (!isCatsWetFoodPage && !isCatsDryFoodPage && !isSnacksPage && !isCatHomePage)
      return availableFilterMap;

    const filteredMap = { ...availableFilterMap };

    // Both cat food pages remove 'sorts' filter
    delete filteredMap.sorts;

    // Only wet food page removes 'line' filter
    if (isCatsWetFoodPage) {
      delete filteredMap.line;
    }

    if (isCatHomePage) {
      delete filteredMap['feed-type'];
    }

    // remove snack-type filter for snack collections as we it's handled within SnackSelector component
    if (isSnacksPage) {
      delete filteredMap['snack-type'];
    }

    return filteredMap;
  }, [availableFilterMap, isCatHomePage, isCatsDryFoodPage, isCatsWetFoodPage, isSnacksPage]);

  const visibleTagsSet = useMemo(
    () => new Set(visibleProducts.flatMap((product) => product.tags.map((tag) => slugify(tag)))),
    [visibleProducts]
  );

  const isFilterAvailable = useCallback(
    (value: possibleTags, filterKey?: string): boolean => {
      // Line filter is always available
      if (filterKey === 'line') return true;
      // Sorts filter is always available (PT-8178)
      if (filterKey === 'sorts') return true;
      return visibleTagsSet.has(value);
    },
    [visibleTagsSet]
  );

  if (Object.keys(filteredAvailableFilterMap).length === 0) return null;

  return (
    <>
      {Object.keys(filteredAvailableFilterMap)
        .filter((filterKey) => filteredAvailableFilterMap[filterKey].tags.length > 0)
        // Hide filters that we don't want to show to the user
        .filter((filterKey) => filterKey !== 'hidden')
        .filter((filterKey) => filterKey !== 'hero-filter')
        // Hide filters that are for the fake door test
        // @see https://petsdeli.atlassian.net/browse/PT-5632
        .filter((filterKey) => !filterKey.includes('fake'))
        .map((filterKey) => (
          <div key={filterKey} className="mb-6">
            <SmallCaps>
              <div data-test={`filtercategory_${filterKey}`} className="mb-4 lg:mb-3">
                <FormattedMessage id={'cdp:filter:' + filterKey} defaultMessage={filterKey} />
              </div>
            </SmallCaps>
            <ul>
              {filteredAvailableFilterMap[filterKey as CollectionFilter]?.tags.map((value) => (
                <li key={value} className="mb-4 text-sm last:mb-0">
                  <label
                    className={cn(
                      'flex items-center',
                      !isFilterAvailable(value, filterKey)
                        ? 'text-grey-400 cursor-default'
                        : 'text-text-primary cursor-pointer'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={appliedFilter.includes(value)}
                      disabled={!isFilterAvailable(value, filterKey)}
                      onChange={() => {
                        toggleFilter(value);

                        if (!isMobile) {
                          const element = ['Filter', filterKey, value];

                          if (appliedFilter.includes(value)) {
                            Gtm.track.unselectElement({ element });
                          } else {
                            Gtm.track.selectElement({ element });
                          }
                        }
                      }}
                      className={cn('mr-2.5', styles.customCheckboxFilter)}
                    />
                    <span data-test={`${filterKey}_filter_option_${value}`} className="text-left">
                      <FormattedMessage
                        id={`cdp:filter:${filterKey}:${value}`}
                        defaultMessage={value}
                      />
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        ))}
    </>
  );
};

export default FilterGroup;
